const KLAVIYO_REVISION = '2024-06-15';
const KLAVIYO_COMPANY_FALLBACK = 'SRn3sm';
const KLAVIYO_SMS_LIST_FALLBACK = 'WWAAA3';

// Asset sync marker: 2026-08-21-sms-consent-fix
const getKlaviyoConfig = () => {
  const config = window.klaviyoConfig || {};

  return {
    companyId: config.companyId || KLAVIYO_COMPANY_FALLBACK,
    smsListId: config.smsListId || KLAVIYO_SMS_LIST_FALLBACK,
  };
};

const normalizeToE164 = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');

  if (digits.length === 10) {
    return `+1${digits}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  return null;
};

const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const getKlaviyoHeaders = () => {
  return {
    accept: 'application/json',
    revision: KLAVIYO_REVISION,
    'content-type': 'application/json',
  };
};

const parseKlaviyoError = async (response, fallback) => {
  try {
    const data = await response.json();
    const detail = data?.errors?.[0]?.detail;

    if (detail) {
      return detail;
    }
  } catch (error) {
    // Ignore JSON parse failures and use the fallback message.
  }

  return fallback;
};

if (!customElements.get('modal-stock-notify')) {
  class ModalStockNotify extends ModalDialog {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.formPanel = this.querySelector('[data-form-panel]');
      this.successPanel = this.querySelector('[data-success-panel]');
      this.successHeading = this.querySelector('[data-success-heading]');
      this.successBody = this.querySelector('[data-success-body]');
      this.errorWrapper = this.querySelector('[data-error-message]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.continueButton = this.querySelector('[data-continue-shopping]');
      this.productTitleDisplay = this.querySelector('[data-product-title-display]');
      this.colorwayDisplay = this.querySelector('[data-colorway-display]');
      this.emailInput = this.querySelector('[name="email"]');
      this.phoneInput = this.querySelector('[name="phone"]');
      this.consentInput = this.querySelector('[name="sms_consent"]');

      this.form.addEventListener('submit', this.onSubmit.bind(this));

      if (this.continueButton) {
        this.continueButton.addEventListener('click', this.hide.bind(this));
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      this.onError(false);

      const email = this.emailInput.value.trim();
      const phoneRaw = this.phoneInput.value.trim();
      const smsConsent = this.consentInput.checked;
      const variantId = this.querySelector('[name="variant"]').value;
      const genericError = this.form.dataset.errorGeneric;

      if (!email && !phoneRaw) {
        this.onError(this.form.dataset.errorMissingContact);
        return;
      }

      if (smsConsent && !phoneRaw) {
        this.onError(this.form.dataset.errorPhone);
        this.phoneInput.setAttribute('aria-invalid', 'true');
        this.phoneInput.focus();
        return;
      }

      if (email && !isValidEmail(email)) {
        this.onError(this.form.dataset.errorEmail);
        this.emailInput.setAttribute('aria-invalid', 'true');
        this.emailInput.focus();
        return;
      }

      this.emailInput.removeAttribute('aria-invalid');

      const phone = phoneRaw ? normalizeToE164(phoneRaw) : null;

      if (phoneRaw && !phone) {
        this.onError(this.form.dataset.errorPhone);
        this.phoneInput.setAttribute('aria-invalid', 'true');
        this.phoneInput.focus();
        return;
      }

      if (phone && !smsConsent) {
        this.onError(this.form.dataset.errorConsent);
        this.consentInput.setAttribute('aria-invalid', 'true');
        this.consentInput.focus();
        return;
      }

      this.phoneInput.removeAttribute('aria-invalid');
      this.consentInput.removeAttribute('aria-invalid');

      const channels = [];
      const profileAttributes = {};

      if (email) {
        channels.push('EMAIL');
        profileAttributes.email = email;
      }

      if (phone) {
        channels.push('SMS');
        profileAttributes.phone_number = phone;
      }

      this.submitButton.disabled = true;
      this.form.setAttribute('aria-busy', 'true');

      try {
        const { companyId, smsListId } = getKlaviyoConfig();
        const bisResponse = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`,
          {
            method: 'POST',
            headers: getKlaviyoHeaders(),
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels,
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: profileAttributes,
                    },
                  },
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: `$shopify:::$default:::${variantId}`,
                    },
                  },
                },
              },
            }),
          }
        );

        if (bisResponse.status !== 202) {
          this.onError(await parseKlaviyoError(bisResponse, genericError));
          return;
        }

        if (phone) {
          // Revision 2024-06-15 rejects nested `subscriptions` on profile.
          // Posting phone (+ optional email) to the SMS list records consent.
          const consentAttributes = {
            phone_number: phone,
          };

          if (email) {
            consentAttributes.email = email;
          }

          const consentResponse = await fetch(
            `https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`,
            {
              method: 'POST',
              headers: getKlaviyoHeaders(),
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    custom_source: 'Back in Stock Modal',
                    profile: {
                      data: {
                        type: 'profile',
                        attributes: consentAttributes,
                      },
                    },
                  },
                  relationships: {
                    list: {
                      data: {
                        type: 'list',
                        id: smsListId,
                      },
                    },
                  },
                },
              }),
            }
          );

          if (consentResponse.status !== 202) {
            this.onError(this.form.dataset.errorSmsConsent);
            return;
          }

          // Klaviyo can return 202 while silently dropping an invalid phone.
          const partialFailure = consentResponse.headers.get('X-Klaviyo-Partial-Failure') || '';

          if (partialFailure.includes('phone_number')) {
            this.onError(this.form.dataset.errorPhone);
            return;
          }
        }

        this.onSuccess();
      } catch (error) {
        this.onError(error.message || genericError);
      } finally {
        this.submitButton.disabled = false;
        this.form.removeAttribute('aria-busy');
      }
    }

    onSuccess() {
      this.onError(false);
      this.formPanel.classList.add('hidden');
      this.successPanel.classList.remove('hidden');
      this.updateSuccessCopy();

      if (this.successHeading) {
        this.successHeading.focus();
      }
    }

    updateSuccessCopy() {
      if (!this.successBody) {
        return;
      }

      const productTitle = this.dataset.productTitle || '';
      const colorway = this.dataset.colorway || '';
      const template = colorway
        ? this.successBody.dataset.successBodyTemplate
        : this.successBody.dataset.successBodyNoColorway;

      this.successBody.textContent = template
        .replace('[product]', productTitle)
        .replace('[colorway]', colorway);
    }

    onError(error) {
      this.errorWrapper.textContent = error || '';
      this.errorWrapper.classList.toggle('hidden', !error);
    }

    syncSelectedProduct({ variantId, productId, productTitle, colorway }) {
      const variantInput = this.querySelector('[name="variant"]');
      const productInput = this.querySelector('[name="product"]');

      if (variantInput && variantId) {
        variantInput.value = variantId;
      }

      if (productInput && productId) {
        productInput.value = productId;
      }

      if (productTitle) {
        this.dataset.productTitle = productTitle;

        if (this.productTitleDisplay) {
          this.productTitleDisplay.textContent = productTitle;
        }
      }

      const nextColorway = colorway || '';
      this.dataset.colorway = nextColorway;

      if (this.colorwayDisplay) {
        this.colorwayDisplay.textContent = nextColorway;
        this.colorwayDisplay.classList.toggle('hidden', !nextColorway);
      }
    }

    reset() {
      this.emailInput.value = '';
      this.phoneInput.value = '';
      this.consentInput.checked = false;
      this.emailInput.removeAttribute('aria-invalid');
      this.phoneInput.removeAttribute('aria-invalid');
      this.consentInput.removeAttribute('aria-invalid');
      this.submitButton.disabled = false;
      this.form.removeAttribute('aria-busy');
      this.onError(false);
      this.successPanel.classList.add('hidden');
      this.formPanel.classList.remove('hidden');
    }

    hide() {
      super.hide();
      this.reset();
    }
  }

  customElements.define('modal-stock-notify', ModalStockNotify);
}
